import { extractThaiMobiles } from './phone.ts';
import { findIsanDistrict, ISAN_PLACES } from './isan-places.ts';
import { THAI_PROVINCES } from './provinces.ts';
import { cleanText } from './text.ts';

/**
 * Reads the registration details a customer types in a chat, in any order and with typos, e.g.
 *   ชื่อที่ม:ช้างน้อยเอฟซี
 *   รุ่น15ปี/วันที่24ตุลา
 *   เบอร์ติดต่อ0812345678
 *   จังหวัดสุรินทร์อำเภอท่าตูม
 * into the form fields. It only suggests: an admin checks and edits every field before saving.
 * Each field says how it was found (`labelled` = next to its keyword, `guessed` = from position/shape).
 */

export type ParsedHow = 'labelled' | 'guessed';

/** The entry system the shop exports to names its age groups "U15(ชาย)" / "U15(หญิง)", so ours must say a gender too. */
export type RegistrationGender = 'male' | 'female';
/** `default` = the customer never said, so the form was filled with ชาย for the admin to correct. */
export type GenderHow = ParsedHow | 'default';
export const GENDER_TH: Record<RegistrationGender, string> = { male: 'ชาย', female: 'หญิง' };
export const DEFAULT_GENDER: RegistrationGender = 'male';

/**
 * One spelling for an age group, so "u14 (ชาย)", "U14(ชาย)" and "Ｕ14（ชาย）" are the same group on both sides.
 * That system matches its own groups by name, and the shop types them by hand at both ends.
 */
export function normalizeAgeGroupLabel(raw: string): string {
  const text = cleanText(raw)
    .replace(/[（]/gu, '(')
    .replace(/[）]/gu, ')')
    .replace(/\s*\(\s*/gu, '(')
    .replace(/\s*\)\s*/gu, ')')
    .replace(/^u\s*-?\s*(?=\d)/iu, 'U')
    .toUpperCase()
    .trim();
  /**
   * "U12(ชาย) u12(ชาย) u12ชาย U12ชาย … ให้ระบบฉลาดรับทราบประเภทที่ควรจะเป็น" (owner, 28 ก.ย. 2569): the gender
   * written in brackets, after a space or glued on is one and the same age group. A label that is not
   * "U<number> + ชาย/หญิง" — ประชาชนทั่วไป, VIP30+ — is a group of its own and is left exactly as it was typed.
   */
  const group = /^U(\d{1,3})\s*\(?\s*(ชาย|หญิง)\s*\)?$/u.exec(text);
  return group ? `U${Number(group[1])}(${group[2]})` : text;
}

/**
 * The key two age groups are the same by. Brackets and spaces are dropped, so a group already saved as "U12ชาย"
 * is found by someone typing "U12(ชาย)" instead of quietly becoming a second group with the same meaning.
 */
export function ageGroupKey(raw: string): string {
  return normalizeAgeGroupLabel(raw).replace(/[()\s]/gu, '');
}

/** "U15" + male → "U15(ชาย)" — the shape the other system matches its own age groups by. */
export function ageGroupLabel(years: number, gender: RegistrationGender = DEFAULT_GENDER): string {
  return `U${years}(${GENDER_TH[gender]})`;
}

export interface ParsedRegistration {
  /** The first team; `teamNames` has every team when the customer registers several with the same details. */
  teamName: string | null;
  teamNames: string[];
  /** Age group with its gender, e.g. "U15(ชาย)"; `ageLabels` has every age group when several are asked for at once. */
  ageLabel: string | null;
  ageLabels: string[];
  ageYears: number | null;
  /** Gender of the age group. Never null: ชาย when the customer did not say (`genderHow: 'default'`). */
  gender: RegistrationGender;
  genderHow: GenderHow;
  /** Competition date, `YYYY-MM-DD` (ค.ศ.). */
  date: string | null;
  phone: string | null;
  province: string | null;
  district: string | null;
  how: Partial<Record<'teamName' | 'ageLabel' | 'date' | 'phone' | 'province' | 'district', ParsedHow>>;
}

/** Lines of the shop's own instructions that customers copy along with their details. */
const INSTRUCTION_LINE = /แจ้งรายละเอียด|ดังต่อไปนี้|กรอกข้อมูล|คัดลอกข้อความ|ส่งให้แอดมิน|ตามรายละเอียด|ยืนยันการสมัคร|สวัสดี/u;

const THAI_DIGITS = /[๐-๙]/g;
const toArabic = (s: string) => s.replace(THAI_DIGITS, (d) => String(d.charCodeAt(0) - 0x0e50));

// Longest names first so "นครราชสีมา" wins over shorter names that it contains.
const MONTHS: Array<[RegExp, number]> = [
  [/มกราคม|มกรา|ม\.ค\.?|มค/u, 1],
  [/กุมภาพันธ์|กุมภา|ก\.พ\.?|กพ/u, 2],
  [/มีนาคม|มีนา|มี\.ค\.?|มีค/u, 3],
  [/เมษายน|เมษา|เม\.ย\.?|เมย/u, 4],
  [/พฤษภาคม|พฤษภา|พ\.ค\.?|พค/u, 5],
  [/มิถุนายน|มิถุนา|มิ\.ย\.?|มิย/u, 6],
  [/กรกฎาคม|กรกฎา|กรกฏา|ก\.ค\.?|กค/u, 7],
  [/สิงหาคม|สิงหา|ส\.ค\.?|สค/u, 8],
  [/กันยายน|กันยา|ก\.ย\.?|กย/u, 9],
  [/ตุลาคม|ตุลา|ต\.ค\.?|ตค/u, 10],
  [/พฤศจิกายน|พฤศจิกา|พ\.ย\.?|พย/u, 11],
  [/ธันวาคม|ธันวา|ธ\.ค\.?|ธค/u, 12],
];
const MONTH_ALT = MONTHS.map(([re]) => re.source).join('|');

// Keywords that start a field; a value runs until the next one.
const KEY = {
  // "ชื่อ" on its own is how many customers label the team ("1️⃣ ชื่อ มาเฮดหยัง × เจ๊รถสั่งลุย"); longest first.
  team: String.raw`(?:(?:ชื่อ\s*)?(?:ทีม|ที่ม|ทิม|ทีมม์|team)|ชื่อ)`,
  // "u" only as a word of its own ("U12", "รุ่น u12"), never inside a name such as "Fu2" or "Bu1".
  age: String.raw`(?:รุ่น(?:อายุ)?|อายุ|ยู\s*(?=\d)|(?<![a-z0-9])u\s*(?=\d))`,
  date: String.raw`(?:วันที่แข่ง(?:ขัน)?|วันแข่ง(?:ขัน)?|วันที่|วัน)`,
  phone: String.raw`(?:เบอร์(?:โทร(?:ศัพท์)?)?(?:ติดต่อ)?|โทร(?:ศัพท์)?|tel|phone)`,
  province: String.raw`(?:จังหวัด|จ\.)`,
  district: String.raw`(?:อำเภอ|อ\.|เขต)`,
};
// Stricter than KEY.date: a plain "วัน" also starts ordinary words and team names.
const ANY_KEY = new RegExp(`${KEY.age}|วันที่|วันแข่ง|${KEY.phone}|${KEY.province}|${KEY.district}`, 'iu');

// Thai has no spaces, so a gender word only counts when Thai letters do not run into it: "ชาย" yes, "ชายแดน" no.
const THAI_LETTER = String.raw`ก-๎`;
const GENDER_WORDS: Array<[string, RegistrationGender]> = [
  // Female first: "women" contains "men".
  [String.raw`หญิง|female|women|girls?`, 'female'],
  [String.raw`ชาย|male|men|boys?`, 'male'],
];
// The nearest word wins, not the first pattern: "U15(ชาย) U15(หญิง)" gives each group its own gender.
const gendersIn = (value: string, loose = false): RegistrationGender | null => {
  let best: { at: number; gender: RegistrationGender } | null = null;
  for (const [alt, gender] of GENDER_WORDS) {
    const re = loose
      ? new RegExp(`(?<![${THAI_LETTER}a-z])(?:${alt})(?![${THAI_LETTER}a-z])`, 'iu')
      : new RegExp(`(?:${alt})`, 'iu');
    const m = re.exec(value);
    if (m && (!best || m.index < best.at)) best = { at: m.index, gender };
  }
  return best?.gender ?? null;
};

const PROVINCE_ALIASES: Array<[string, string]> = [
  ['กรุงเทพฯ', 'กรุงเทพมหานคร'],
  ['กรุงเทพ', 'กรุงเทพมหานคร'],
  ['กทม.', 'กรุงเทพมหานคร'],
  ['กทม', 'กรุงเทพมหานคร'],
  ['อยุธยา', 'พระนครศรีอยุธยา'],
  ['โคราช', 'นครราชสีมา'],
];
const PROVINCE_NAMES: Array<[string, string]> = [...THAI_PROVINCES.map((p) => [p, p] as [string, string]), ...PROVINCE_ALIASES].sort((a, b) => b[0].length - a[0].length);

function tidy(value: string, max = 80): string | null {
  const v = cleanText(value)
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}️⃣]/gu, '')
    .replace(/^[\s:：=\-–.,/]+|[\s:：=\-–.,/]+$/gu, '')
    .trim()
    .slice(0, max);
  return v.length >= 2 ? v : null;
}

/** Cut a value at the next field keyword ("ช้างน้อยเอฟซี รุ่น15" → "ช้างน้อยเอฟซี"). */
function untilNextKey(value: string): string {
  const m = ANY_KEY.exec(value);
  return m && m.index > 0 ? value.slice(0, m.index) : m?.index === 0 ? '' : value;
}

function iso(y: number, m: number, d: number): string | null {
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Year as written (2569, 69, 2026, 26) → ค.ศ.; missing → the next such date from `today` (a date up to 30 days ago stays this year). */
function resolveYear(written: string | undefined, month: number, day: number, today: string): number {
  const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];
  if (written) {
    let y = Number(written);
    if (written.length <= 2) y = y >= 50 ? 2500 + y : 2000 + y;
    if (y >= 2400) y -= 543;
    return y;
  }
  const thisYear = Date.UTC(ty, month - 1, day);
  const cutoff = Date.UTC(ty, tm - 1, td) - 30 * 86_400_000;
  return thisYear >= cutoff ? ty : ty + 1;
}

export function parseRegistrationText(input: string, today: string): ParsedRegistration {
  const out: ParsedRegistration = { teamName: null, teamNames: [], ageLabel: null, ageLabels: [], ageYears: null, gender: DEFAULT_GENDER, genderHow: 'default', date: null, phone: null, province: null, district: null, how: {} };
  const text = toArabic(input.normalize('NFC')).replace(/\r/g, '');
  if (!cleanText(text)) return out;
  // Numbered form ("1️⃣ …", "2) …") → one field per line.
  const lines = text
    .split('\n')
    .map((l) => l.replace(/^\s*(?:[1-9]\s*️?⃣|[1-9]\s*[.)]\s+|[-•*]\s*)/u, '').trim())
    .filter(Boolean);
  const flat = lines.join('\n');

  // Phone: any Thai mobile.
  const phone = extractThaiMobiles(flat)[0];
  if (phone?.national) {
    out.phone = phone.national;
    out.how.phone = new RegExp(`${KEY.phone}`, 'iu').test(flat) ? 'labelled' : 'guessed';
  }
  // Without phone digits the other numbers are easier to read.
  const noPhone = phone ? flat.replace(/(?<![\d+])(?:\+?66[\s.-]?|0)[689](?:[\s.-]?\d){8}(?!\d)/g, ' ') : flat;

  // Age group: "U12", "ยู12", "รุ่น 15 ปี", "รุ่นอายุไม่เกิน 12", "12 ปี".
  const ageRe = [
    { re: /(?:^|[^a-z0-9])(?:u|ยู)\s*-?\s*(\d{1,2})(?!\d)/iu, how: 'labelled' as const },
    { re: /(?:รุ่น|อายุ)\s*(?:อายุ)?\s*(?:ไม่เกิน|ต่ำกว่า)?\s*(\d{1,2})(?!\d)/u, how: 'labelled' as const },
    { re: /(?<!\d)(\d{1,2})\s*(?:ปี|ขวบ)/u, how: 'guessed' as const },
  ];
  // Gender, in order of how sure it is: the word "เพศ", then a word next to the age group ("U14 ชาย", "U14(หญิง)"),
  // then the word on its own anywhere. The shop asks for it now, but many customers still leave it out.
  const labelledGender = /เพศ\s*[:：=\-–]?\s*([^\n]{0,16})/u.exec(noPhone);
  const saidGender = labelledGender ? gendersIn(labelledGender[1] ?? '') : null;
  if (saidGender) {
    out.gender = saidGender;
    out.genderHow = 'labelled';
  }

  for (const { re, how } of ageRe) {
    const m = re.exec(noPhone);
    const n = m ? Number(m[1]) : NaN;
    if (n >= 5 && n <= 60) {
      out.ageYears = n;
      out.how.ageLabel = how;
      // The rest of that line: "U14 ชาย", "U14(หญิง)", "รุ่น 12 ปี ชาย".
      if (out.genderHow !== 'labelled') {
        const rest = noPhone.slice((m?.index ?? 0) + (m?.[0] ?? '').length).split('\n')[0] ?? '';
        const near = gendersIn(rest.slice(0, 20), true);
        if (near) {
          out.gender = near;
          out.genderHow = 'labelled';
        }
      }
      break;
    }
  }
  // Still nothing: the word standing on its own anywhere ("ทีมหญิง" is not it — Thai runs words together).
  if (out.genderHow === 'default') {
    const loose = gendersIn(noPhone, true);
    if (loose) {
      out.gender = loose;
      out.genderHow = 'guessed';
    }
  }
  if (out.ageYears !== null) out.ageLabel = ageGroupLabel(out.ageYears, out.gender);

  // Several age groups at once: "U12, U14", "รุ่น 10 และ 12", "U12/U14", "U15(ชาย) U15(หญิง)".
  if (out.ageLabel) {
    const all = new Set<string>([out.ageLabel]);
    for (const m of noPhone.matchAll(/(?:^|[^a-z0-9])(?:u|ยู)\s*-?\s*(\d{1,2})(?!\d)/giu)) {
      const n = Number(m[1]);
      // Each group may carry its own gender, which is how that entry system splits "U15(ชาย)" from "U15(หญิง)".
      const rest = noPhone.slice((m.index ?? 0) + m[0].length).split('\n')[0] ?? '';
      if (n >= 5 && n <= 60) all.add(ageGroupLabel(n, gendersIn(rest.slice(0, 20), true) ?? out.gender));
    }
    const listed = /(?:รุ่น|อายุ)\s*((?:\d{1,2}\s*(?:ปี|ขวบ)?\s*(?:,|และ|กับ|\/|\+|\s)\s*)+\d{1,2})(?!\d)/u.exec(noPhone);
    for (const d of listed?.[1]?.match(/\d{1,2}/g) ?? []) {
      const n = Number(d);
      if (n >= 5 && n <= 60) all.add(ageGroupLabel(n, out.gender));
    }
    out.ageLabels = [...all].slice(0, 5);
  }

  // Date: "24 ตุลา", "24ต.ค.69", "วันที่ 24/10", "24/10/2569".
  const named = new RegExp(`(?<!\\d)(\\d{1,2})\\s*(?:${MONTH_ALT})\\s*(\\d{4}|\\d{2}(?!\\d))?`, 'u').exec(noPhone);
  if (named) {
    const monthText = named[0].replace(/^\d{1,2}\s*/, '');
    const month = MONTHS.find(([re]) => re.test(monthText))?.[1];
    const day = Number(named[1]);
    if (month) {
      const y = resolveYear(named[2], month, day, today);
      out.date = iso(y, month, day);
    }
  }
  if (!out.date) {
    const numeric = /(?:วันที่|วันแข่ง|วัน)\s*(\d{1,2})\s*[/.-]\s*(\d{1,2})(?:\s*[/.-]\s*(\d{2,4}))?/u.exec(noPhone) ?? /(?<![\d/])(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?![\d/])/u.exec(noPhone);
    if (numeric) {
      const day = Number(numeric[1]);
      const month = Number(numeric[2]);
      if (month >= 1 && month <= 12) out.date = iso(resolveYear(numeric[3], month, day, today), month, day);
    }
  }
  if (out.date) out.how.date = new RegExp(KEY.date, 'u').test(noPhone) ? 'labelled' : 'guessed';

  // Province: a known name, preferably right after "จังหวัด"/"จ.".
  const compact = noPhone.replace(/\s+/g, '');
  const afterKey = new RegExp(`${KEY.province}\\s*`, 'gu');
  for (const m of noPhone.matchAll(afterKey)) {
    const rest = noPhone.slice((m.index ?? 0) + m[0].length).replace(/\s+/g, '');
    const hit = PROVINCE_NAMES.find(([name]) => rest.startsWith(name));
    if (hit) {
      out.province = hit[1];
      out.how.province = 'labelled';
      break;
    }
  }
  if (!out.province) {
    const hit = PROVINCE_NAMES.find(([name]) => compact.includes(name));
    if (hit) {
      out.province = hit[1];
      out.how.province = 'guessed';
    }
  }

  // District: the word after "อำเภอ"/"อ."/"เขต", up to the next keyword or line end.
  const dist = new RegExp(`${KEY.district}\\s*([^\\n]+)`, 'u').exec(noPhone);
  if (dist) {
    let value = untilNextKey(dist[1] ?? '');
    if (out.province) value = value.replace(out.province, '');
    const name = tidy(value.split(/[\s,]/u)[0] ?? '', 40);
    if (name) {
      out.district = name;
      out.how.district = 'labelled';
    }
  }

  // Isan districts (v1.100.0): "อ.เมือง" + จ.ร้อยเอ็ด → "เมืองร้อยเอ็ด"; a district written without "อ." is found by
  // name (in the province given, else unique across Isan and long enough not to be an ordinary word); a district
  // without a province gives the province.
  if (out.district) {
    const hit = findIsanDistrict(out.district, out.province);
    if (hit) out.district = hit.district;
  } else {
    const candidates = ISAN_PLACES.filter((p) => p.district && (out.province ? p.province === out.province && p.district.length >= 3 : p.district.length >= 4) && compact.includes(p.district));
    const longest = candidates.sort((a, b) => b.district.length - a.district.length)[0];
    const unique = longest && candidates.filter((c) => c.district === longest.district).length === 1;
    if (longest && unique) {
      out.district = longest.district;
      out.how.district = 'guessed';
    }
  }
  if (out.district && !out.province) {
    const hit = findIsanDistrict(out.district);
    if (hit) {
      out.province = hit.province;
      out.how.province = 'guessed';
    }
  }

  // Team: a labelled line ("ชื่อทีม:", "ชื่อที่ม:", "ทีม"), else the first line that is not another field.
  // Several teams with the same details: "ทีม ดาวเหนือ A, ดาวเหนือ B", "ทีม A และ ทีม B", or one labelled line per team.
  const teamRe = new RegExp(`${KEY.team}\\s*\\d?\\s*[:：=\\-–]?\\s*([^\\n]+)`, 'giu');
  const leadingKey = new RegExp(`^${KEY.team}\\s*`, 'iu');
  for (const labelled of flat.matchAll(teamRe)) {
    for (const part of untilNextKey(labelled[1] ?? '').split(/\s*(?:,|，|และ|กับ|\+)\s*/u)) {
      const name = tidy(part.replace(leadingKey, ''));
      if (name && !extractThaiMobiles(name).length && !out.teamNames.includes(name) && out.teamNames.length < 5) out.teamNames.push(name);
    }
  }
  if (out.teamNames.length) {
    out.teamName = out.teamNames[0]!;
    out.how.teamName = 'labelled';
  }
  const otherFields = [out.phone, out.ageLabel, out.date, out.province].filter(Boolean).length;
  if (!out.teamName && otherFields >= 2) {
    for (const line of lines) {
      if (ANY_KEY.test(line) || extractThaiMobiles(line).length) continue;
      // The customer copies our own instructions ("แจ้งรายละเอียดดังต่อไปนี้ได้เลยค่ะ 👇"): never a team name.
      if (INSTRUCTION_LINE.test(line)) continue;
      if (/^[\d\s./:-]+$/.test(line)) continue;
      if (PROVINCE_NAMES.some(([n]) => line.replace(/\s+/g, '') === n)) continue;
      const name = tidy(line);
      if (name) {
        out.teamName = name;
        out.teamNames = [name];
        out.how.teamName = 'guessed';
        break;
      }
    }
  }
  return out;
}

/**
 * The match day written in a category's name ("นาตาชู เสาร์ที่ 3 ตุลาคม 2569" -> "2026-10-03"), or null.
 *
 * The shop names a category after the day it is for, and that name is the only thing tying the two together —
 * there is no column for it. Both sides read it the same way because they read it here.
 */
export function folderMatchDate(name: string, today: string): string | null {
  return parseRegistrationText(name, today).date ?? null;
}
